import React from 'react';
import {useTranslation} from 'react-i18next';

import {Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import AddIcon from '@edna/icons/add.svg';

import {Text} from 'src/components/primitives';
import {TItemId} from 'src/constants';
import {CloseIcon, News, NewsImage} from 'src/containers/News';
import {
  useGetCurrentUserQuery,
  useGetSpecialOffersQuery,
  useGetTenantDataQuery,
} from 'src/containers/User';
import {useAuthorizedMetrics} from 'src/hooks';
import {FlowSpecialOfferImage, SmsSpecialOfferImage} from 'src/images';

import {ESpecialOffer} from './definitions';

type TMailData = {
  subject?: string;
  cc?: string;
  body?: string;
};

const ConnectButton = React.memo<{mailData: TMailData; onClick?: TAnyFunction}>(
  ({mailData, onClick}) => {
    const {t} = useTranslation();
    const {data: tenantData} = useGetTenantDataQuery();

    const mailto = React.useMemo(() => {
      const data = {
        cc: tenantData?.registrationEmail,
        ...mailData,
      };

      const queryParams = Object.entries(data)
        .map(([key, value = '']) => `${key}=${encodeURIComponent(value)}`)
        .join('&');

      return `mailto:${tenantData?.personalManagerEmail}?${queryParams}`;
    }, [tenantData, mailData]);

    return (
      <Button appearance="primary" size="XS" href={mailto} as="a" onClick={onClick}>
        <Stack direction="ROW" size="1" align="center">
          <Icon as={AddIcon} />
          {t('Notifications:specialOffers.connect')}
        </Stack>
      </Button>
    );
  },
);

const SpecialOffer = React.memo<{
  id: TItemId;
  title: React.ReactNode;
  image: TSvgComponentType;
  mailData: TMailData;
  onRequest?: TAnyFunction;
}>(({id, title, image, mailData, onRequest}) => {
  return (
    <News id={id} noHighlight>
      {({onClose}) => (
        <Stack direction="ROW" size="4" isFullWidth>
          <NewsImage image={image} />
          <Stack size="2" isFullWidth>
            <Text size="M" weight={500} color="black">
              {title}
            </Text>
            <ConnectButton mailData={mailData} onClick={onRequest} />
          </Stack>
          <CloseIcon onClick={onClose} />
        </Stack>
      )}
    </News>
  );
});

const SmsSpecialOffer = React.memo(() => {
  const {sendMetrics} = useAuthorizedMetrics();
  const {t} = useTranslation();

  const {data: user} = useGetCurrentUserQuery();
  const {data: tenant} = useGetTenantDataQuery();

  return (
    <SpecialOffer
      id={ESpecialOffer.SMS}
      image={SmsSpecialOfferImage}
      title={t(`Notifications:specialOffers.offer.${ESpecialOffer.SMS}.title`)}
      mailData={{
        subject: t(`Notifications:specialOffers.mail.${ESpecialOffer.SMS}.subject`, {
          login: user?.login,
          companyName: tenant?.companyName,
        }),
        body: t(`Notifications:specialOffers.mail.${ESpecialOffer.SMS}.body`),
      }}
      onRequest={() =>
        sendMetrics({
          id: 'company-profile-special-offer-request',
          params: {
            special_offer: ESpecialOffer.SMS,
          },
        })
      }
    />
  );
});

const FlowSpecialOffer = React.memo(() => {
  const {sendMetrics} = useAuthorizedMetrics();
  const {t} = useTranslation();

  const {data: user} = useGetCurrentUserQuery();
  const {data: tenant} = useGetTenantDataQuery();

  return (
    <SpecialOffer
      id={ESpecialOffer.FLOW}
      image={FlowSpecialOfferImage}
      title={t(`Notifications:specialOffers.offer.${ESpecialOffer.FLOW}.title`)}
      mailData={{
        subject: t(`Notifications:specialOffers.mail.${ESpecialOffer.FLOW}.subject`, {
          login: user?.login,
          companyName: tenant?.companyName,
        }),
        body: t(`Notifications:specialOffers.mail.${ESpecialOffer.FLOW}.body`),
      }}
      onRequest={() =>
        sendMetrics({
          id: 'company-profile-special-offer-request',
          params: {
            special_offer: ESpecialOffer.FLOW,
          },
        })
      }
    />
  );
});

const SPECIAL_OFFERS_MAP = {
  [ESpecialOffer.SMS]: SmsSpecialOffer,
  [ESpecialOffer.FLOW]: FlowSpecialOffer,
};

const SpecialOffers = React.memo(() => {
  const {data: specialOffers = []} = useGetSpecialOffersQuery();

  if (!specialOffers.length) {
    return null;
  }

  return (
    <Stack size="2">
      {specialOffers
        .filter((offer) => !!SPECIAL_OFFERS_MAP[offer])
        .map((offer) => {
          const Offer = SPECIAL_OFFERS_MAP[offer];

          return <Offer key={offer} />;
        })}
    </Stack>
  );
});

export {SmsSpecialOffer, FlowSpecialOffer, SpecialOffers};
